// frontend\src\pages\ScanRecord.js
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../api/api";
import { GET_FILMS_API, GET_DEVELOPERS_API, SCANS_API } from "../constants";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BiArrowBack } from "react-icons/bi";
import LensesInput from "../components/LensesInput";
import Loader from "../components/Loader";
import ScrollToTopButton from "../components/ScrollToTopButton";

const ScanRecord = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [scan, setScan] = useState({
    scan_date: "",
    location: "",
    camera: "",
    lenses: "",
    film: "",
    development_type: "home",
    developer: "",
    development_time: "",
    lab_name: "",
    comments: "",
    link: "",
  });
  const [meta, setMeta] = useState({
    created_at: null,
    updated_at: null,
  });
  const [films, setFilms] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [lensesOptions, setLensesOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(!!id);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});

  const fetchScan = useCallback(async () => {
    try {
      const response = await api.get(`${SCANS_API}${id}/`);
      setScan(response.data);
      setMeta({
        created_at: response.data.created_at,
        updated_at: response.data.updated_at,
      });
    } catch (error) {
      console.error("Ошибка загрузки:", error);
      toast.error("Ошибка загрузки данных!", { position: "top-right" });
      navigate("/catalog");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  const loadData = useCallback(async () => {
    try {
      const [filmsRes, developersRes, camerasRes, lensesRes] =
        await Promise.all([
          api.get(GET_FILMS_API),
          api.get(GET_DEVELOPERS_API),
          api.get(`${SCANS_API}cameras/`),
          api.get(`${SCANS_API}lenses/`),
        ]);

      setFilms(Array.isArray(filmsRes?.data) ? filmsRes.data : []);
      setDevelopers(
        Array.isArray(developersRes?.data) ? developersRes.data : []
      );
      setCameras(Array.isArray(camerasRes?.data) ? camerasRes.data : []);
      setLensesOptions(Array.isArray(lensesRes?.data) ? lensesRes.data : []);
    } catch (error) {
      console.error("Ошибка загрузки данных:", error);
      toast.error("Ошибка загрузки данных!", { position: "top-right" });
    } finally {
      setIsDataLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    if (id) {
      fetchScan();
    } else {
      // Обработка копирования записи
      if (location.state?.copyFrom) {
        const { copyFrom } = location.state;
        // Создаем новый объект, явно исключая все системные поля
        const { id, user, created_at, updated_at, ...copyData } = copyFrom;
        setScan({
          ...copyData,
          scan_date: new Date().toISOString().split("T")[0], // Обновляем дату на текущую
        });
      }
      setIsLoading(false);
    }
  }, [id, loadData, fetchScan, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setScan((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    try {
      const dataToSend = {
        ...scan,
        development_time:
          scan.development_type === "home" ? scan.development_time : null,
        developer: scan.development_type === "home" ? scan.developer : null,
        lab_name: scan.development_type === "lab" ? scan.lab_name : null,
      };

      if (id) {
        await api.put(`${SCANS_API}${id}/`, dataToSend);
        toast.success("Запись успешно обновлена!", { position: "top-right" });
      } else {
        await api.post(SCANS_API, dataToSend);
        toast.success("Запись успешно создана!", { position: "top-right" });
      }
      navigate("/catalog");
    } catch (error) {
      console.error("Ошибка сохранения:", error);

      if (error.response?.status === 400) {
        if (error.response.data) {
          setValidationErrors(error.response.data);
          toast.error("Пожалуйста, исправьте ошибки в форме", {
            position: "top-right",
          });
        } else {
          toast.error("Некорректные данные. Проверьте заполнение полей", {
            position: "top-right",
          });
        }
      } else {
        toast.error("Ошибка сохранения записи!", { position: "top-right" });
      }
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("ru-RU");
  };

  if (isLoading || isDataLoading) {
    return <Loader />;
  }

  return (
    <div className="container mt-4">
      <h2>{id ? `Редактирование записи #${id}` : "Новая запись"}</h2>

      <form onSubmit={handleSubmit}>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label className="form-label">Дата сканирования*</label>
            <input
              type="date"
              className={`form-control ${
                validationErrors.scan_date ? "is-invalid" : ""
              }`}
              name="scan_date"
              value={scan.scan_date}
              onChange={handleChange}
              required
            />
            {validationErrors.scan_date && (
              <div className="invalid-feedback">
                {validationErrors.scan_date}
              </div>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label">Локация*</label>
            <input
              type="text"
              className={`form-control ${
                validationErrors.location ? "is-invalid" : ""
              }`}
              name="location"
              value={scan.location}
              onChange={handleChange}
              required
            />
            {validationErrors.location && (
              <div className="invalid-feedback">
                {validationErrors.location}
              </div>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label">Камера</label>
            <input
              list="cameras-list"
              className={`form-control ${
                validationErrors.camera ? "is-invalid" : ""
              }`}
              name="camera"
              value={scan.camera}
              onChange={handleChange}
              placeholder="Выберите или введите вручную"
            />
            <datalist id="cameras-list">
              {cameras.map((camera) => (
                <option key={camera} value={camera} />
              ))}
            </datalist>
            {validationErrors.camera && (
              <div className="invalid-feedback">{validationErrors.camera}</div>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label">Фотоплёнка*</label>
            <input
              list="films-list"
              className={`form-control ${
                validationErrors.film ? "is-invalid" : ""
              }`}
              name="film"
              value={scan.film}
              onChange={handleChange}
              placeholder="Выберите или введите вручную"
              required
            />
            <datalist id="films-list">
              {films.map((film) => (
                <option key={film.film_id} value={film.film_name} />
              ))}
            </datalist>
            {validationErrors.film && (
              <div className="invalid-feedback">{validationErrors.film}</div>
            )}
          </div>

          <div className="col-12">
            <label className="form-label">Объективы</label>
            <div
              className="lenses-input-container"
              style={{ position: "relative" }}
            >
              <LensesInput
                value={scan.lenses || ""}
                options={lensesOptions}
                onChange={(value) => {
                  setScan((prev) => ({ ...prev, lenses: value }));
                  if (validationErrors.lenses) {
                    setValidationErrors((prev) => ({ ...prev, lenses: null }));
                  }
                }}
                placeholder="Введите объективы через запятую или выберите из списка"
              />
            </div>
            {validationErrors.lenses && (
              <div className="invalid-feedback d-block">
                {validationErrors.lenses}
              </div>
            )}
          </div>

          <div className="col-12">
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="development_type"
                id="development_home"
                value="home"
                checked={scan.development_type === "home"}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="development_home">
                Проявка дома
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="development_type"
                id="development_lab"
                value="lab"
                checked={scan.development_type === "lab"}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="development_lab">
                Проявка в лаборатории
              </label>
            </div>
            {validationErrors.development_type && (
              <div className="text-danger small">
                {validationErrors.development_type}
              </div>
            )}
          </div>

          {scan.development_type === "home" ? (
            <>
              <div className="col-md-4">
                <label className="form-label">Проявитель</label>
                <input
                  list="developers-list"
                  className={`form-control ${
                    validationErrors.developer ? "is-invalid" : ""
                  }`}
                  name="developer"
                  value={scan.developer || ""}
                  onChange={handleChange}
                  placeholder="Название проявителя"
                />
                <datalist id="developers-list">
                  {developers.map((dev) => (
                    <option key={dev.dev_id} value={dev.dev_name} />
                  ))}
                </datalist>
                {validationErrors.developer && (
                  <div className="invalid-feedback">
                    {validationErrors.developer}
                  </div>
                )}
              </div>

              <div className="col-md-2">
                <label className="form-label">Пропорция</label>
                <input
                  type="text"
                  className={`form-control ${
                    validationErrors.dilution ? "is-invalid" : ""
                  }`}
                  name="dilution"
                  value={scan.dilution || ""}
                  onChange={handleChange}
                  placeholder="1:50, stock и т.д."
                />
                {validationErrors.dilution && (
                  <div className="invalid-feedback">
                    {validationErrors.dilution}
                  </div>
                )}
              </div>

              <div className="col-md-2">
                <label className="form-label">Время (мин)</label>
                <input
                  type="number"
                  className={`form-control ${
                    validationErrors.development_time ? "is-invalid" : ""
                  }`}
                  name="development_time"
                  value={scan.development_time || ""}
                  onChange={handleChange}
                  step="0.1"
                  placeholder="Не указано"
                />
                {validationErrors.development_time && (
                  <div className="invalid-feedback">
                    {validationErrors.development_time}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="col-md-6">
              <label className="form-label">Название лаборатории*</label>
              <input
                type="text"
                className={`form-control ${
                  validationErrors.lab_name ? "is-invalid" : ""
                }`}
                name="lab_name"
                value={scan.lab_name}
                onChange={handleChange}
                placeholder="Название лаборатории"
              />
              {validationErrors.lab_name && (
                <div className="invalid-feedback">
                  {validationErrors.lab_name}
                </div>
              )}
            </div>
          )}

          <div className="col-12">
            <label className="form-label">Ссылка на сканы</label>
            <input
              type="url"
              className={`form-control ${
                validationErrors.link ? "is-invalid" : ""
              }`}
              name="link"
              value={scan.link}
              onChange={handleChange}
              placeholder="https://example.com"
            />
            {validationErrors.link && (
              <div className="invalid-feedback">{validationErrors.link}</div>
            )}
          </div>

          <div className="col-12">
            <label className="form-label">Комментарии</label>
            <textarea
              className={`form-control ${
                validationErrors.comments ? "is-invalid" : ""
              }`}
              name="comments"
              value={scan.comments}
              onChange={handleChange}
              rows="3"
              placeholder="Дополнительные заметки о съёмке или проявке"
            />
            {validationErrors.comments && (
              <div className="invalid-feedback">
                {validationErrors.comments}
              </div>
            )}
          </div>
        </div>

        {id && (
          <div className="mb-3 text-muted small">
            <div>Создано: {formatDateTime(meta.created_at)}</div>
            <div>Обновлено: {formatDateTime(meta.updated_at)}</div>
          </div>
        )}

        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={() => navigate("/catalog")}
          >
            <BiArrowBack className="me-1" /> Назад
          </button>

          <button type="submit" className="btn btn-primary">
            {id ? "Сохранить изменения" : "Создать запись"}
          </button>
        </div>
      </form>
      <ScrollToTopButton/>
    </div>
  );
};

export default ScanRecord;
